// Service Worker Registration Utility

// Global state for error recovery
let errorRecoveryState = {
  isVisible: false,
  errorType: 'white-screen' as
    | 'white-screen'
    | 'resource-error'
    | 'javascript-error'
    | 'react-error',
  onRetry: () => {},
};

// Function to show error recovery screen
export const showErrorRecovery = (
  errorType:
    | 'white-screen'
    | 'resource-error'
    | 'javascript-error'
    | 'react-error' = 'white-screen',
  onRetry?: () => void,
) => {
  errorRecoveryState = {
    isVisible: true,
    errorType,
    onRetry: onRetry || (() => window.location.reload()),
  };

  // Dispatch custom event to notify components
  window.dispatchEvent(
    new CustomEvent('pwa-error-recovery', {
      detail: errorRecoveryState,
    }),
  );
};

// Function to hide error recovery screen
export const hideErrorRecovery = () => {
  errorRecoveryState.isVisible = false;
  window.dispatchEvent(
    new CustomEvent('pwa-error-recovery', {
      detail: errorRecoveryState,
    }),
  );
};

export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/sw.js`;

      navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
          console.log('Service Worker registered successfully:', registration);

          // Check for updates on registration
          registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (installingWorker) {
              installingWorker.onstatechange = () => {
                if (installingWorker.state === 'installed') {
                  if (navigator.serviceWorker.controller) {
                    // New service worker is ready
                    console.log('New service worker available');
                    // Trigger CHECK_FOR_UPDATE to notify clients
                    registration.installing?.postMessage({
                      type: 'CHECK_FOR_UPDATE',
                    });
                  }
                }
              };
            }
          };

          // Simple update checking for PWA mode
          const isPWA = () => {
            return (
              window.matchMedia('(display-mode: standalone)').matches ||
              (window.navigator as any).standalone === true
            );
          };

          // Check for updates periodically
          const checkForUpdates = async () => {
            try {
              await registration.update();

              if (registration.waiting) {
                console.log(
                  'New service worker waiting, activating immediately',
                );
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });

                if (isPWA()) {
                  console.log(
                    'PWA mode detected, forcing reload for immediate update',
                  );
                  window.location.reload();
                }
              }
            } catch (error) {
              console.error('Service Worker update check failed:', error);
            }
          };

          // Check for updates immediately
          checkForUpdates();

          // Check for updates periodically (every 5 minutes)
          setInterval(checkForUpdates, 5 * 60 * 1000);

          // Handle update found
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (
                  newWorker.state === 'installed' &&
                  navigator.serviceWorker.controller
                ) {
                  console.log('New service worker installed and waiting');
                }
              });
            }
          });

          // Handle controller change
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('Service Worker controller changed');
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    });
  }
};

export const unregisterServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error('Service Worker unregistration failed:', error);
      });
  }
};
